import { FACEBOOK, INSTAGRAM } from '@/config/constants';
import { PLATFORM_STATIC_ICONS_PNG } from '@/config/icons';

import { previewProfileFallbackName } from './constants';
import type { AdManagerPreviewProfile, AdPreviewPlatform } from './types';

const getPreviewPlatformFromPlacement = (
  placementKey: string
): AdPreviewPlatform =>
  placementKey.startsWith(INSTAGRAM) ? INSTAGRAM : FACEBOOK;

const getPlatformProfiles = (
  platformsInformation: Record<string, any> | null | undefined,
  platform: AdPreviewPlatform
) => {
  const profiles = platformsInformation?.[platform];
  if (Array.isArray(profiles)) return profiles;
  if (profiles && typeof profiles === 'object') return [profiles];
  return [];
};

const getNestedImageUrl = (value: any): string | undefined => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return undefined;

  return (
    value.url ||
    value.data?.url ||
    value.image_url ||
    value.profile_picture_url ||
    undefined
  );
};

const getProfileImageUrl = (profile: any): string | undefined =>
  [
    profile?.profile_image_url,
    profile?.profile_picture_url,
    profile?.profile_picture,
    profile?.picture_url,
    profile?.image_url,
    profile?.image,
    profile?.picture
  ]
    .map(getNestedImageUrl)
    .find(Boolean);

const getProfileName = (
  profile: any,
  platform: AdPreviewPlatform,
  fallbackName = ''
) =>
  profile?.display_name ||
  profile?.profile_name ||
  profile?.page_name ||
  profile?.name ||
  profile?.username ||
  fallbackName ||
  previewProfileFallbackName[platform];

export const getAdManagerPreviewProfile = (
  platformsInformation: Record<string, any> | null | undefined,
  placementKey: string,
  fallbackName = '',
  fallbackPlatformNames: Partial<Record<AdPreviewPlatform, string>> = {}
): AdManagerPreviewProfile => {
  const platform = getPreviewPlatformFromPlacement(placementKey);
  const profile = getPlatformProfiles(platformsInformation, platform)[0] || {};
  const platformFallbackName =
    fallbackPlatformNames[platform] || previewProfileFallbackName[platform];

  return {
    imageUrl: getProfileImageUrl(profile) || PLATFORM_STATIC_ICONS_PNG[platform],
    name: getProfileName(profile, platform, fallbackName || platformFallbackName),
    platform
  };
};
